/** Role labels and profile URLs for the catch-all entity columns (memo §15 items 1 + 2).
 *
 * Leaf module: no React, no AG Grid, so both the grid cell renderer and the record panel can use it
 * and its formatting is testable without mounting anything.
 */

/** The two fact-backed catch-all columns. Mirrors `entity_catchall_columns.CATCHALL_DEFS`.
 *
 * Named individually as well as collected, because P3's contradiction check reads ACROSS the two —
 * a person's side is compared against their employer's, and the employer lives in the other column. */
export const CATCHALL_ORGS_FIELD_KEY = 'all_orgs';
export const CATCHALL_PEOPLE_FIELD_KEY = 'all_people';
export const CATCHALL_FIELD_KEYS = new Set([CATCHALL_ORGS_FIELD_KEY, CATCHALL_PEOPLE_FIELD_KEY]);

/**
 * A vocabulary slug as a human label: `investment_banking` → `Investment Banking`.
 *
 * **The wire value is a slug and the prettifying happens HERE, deliberately.** The server admits a
 * role through the governed gate by SHAPE (`^[a-z0-9][a-z0-9_]{0,39}$`) precisely because a slug
 * cannot carry prose — that shape test is what stops a name, an email or free text riding along on a
 * governed link. Formatting server-side would mean sending display strings through the gate and
 * losing the check.
 *
 * Acronyms the vocabulary uses are cased explicitly; everything else is word-cased.
 */
const ACRONYMS: Record<string, string> = { ir: 'IR', vdr: 'VDR', ma: 'M&A', ceo: 'CEO', cfo: 'CFO' };

export function formatRole(slug: string): string {
  return slug
    .split('_')
    .filter((w) => w !== '')
    .map((w) => ACRONYMS[w] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * The role E5a assigns to a converted legacy ref — deliberately meaning "nobody has said yet".
 *
 * ⚠ **It must never render as a label.** E5a converts every imported Affinity ref as
 * `deal_party_role='unclassified'` (~387 org refs), so a naive renderer would put "Unclassified" on
 * almost every chip on the board and a heading of the same name over almost every panel group —
 * shouting the absence of information. `unclassified` is the absence of a role, so it renders exactly
 * as an absent role does: no label, no heading.
 */
export const UNCLASSIFIED_ROLE = 'unclassified';

/** Does this slug say anything? `null` / absent / `unclassified` do not. */
export function isMeaningfulRole(slug: string | null | undefined): boolean {
  return typeof slug === 'string' && slug !== '' && slug !== UNCLASSIFIED_ROLE;
}

/**
 * Party-facts spec §5 rule 2 — does this ref RENDER this role slug, or fold it away?
 *
 * **The server now answers this and `implied_roles` is its answer.** The fold is a statement about
 * what ELSE the entity holds on this deal ("it is the legal adviser, so 'participant' adds nothing"),
 * which is a vocabulary question — `implied_by` in the governed register — that no renderer can answer
 * from a slug in isolation. `deal_field_projection` computes it once, and the pre-hydrate and hydrated
 * views read the same key, which is what keeps them from disagreeing (the "jumpy display twice" bug).
 *
 * ⚠ **`isMeaningfulRole` stays as the FALLBACK, and dropping it would be a regression, not a
 * cleanup.** A cell projected before `crm_party_display_fold_enabled` was flipped carries no
 * `implied_roles` at all, and `unclassified` would then start rendering as a heading over every one of
 * the ~387 converted legacy refs — exactly the "shouting the absence of information" this module was
 * written to stop. So: the client rule always, plus the server's list whenever it is there.
 */
export function rendersRole(
  ref: { implied_roles?: string[] }, slug: string | null | undefined,
): boolean {
  if (!isMeaningfulRole(slug)) return false;
  return !(ref.implied_roles ?? []).includes(slug as string);
}

/** `['legal','investment_banking']` → `'Legal, Investment Banking'`; nothing meaningful → null.
 *
 * `impliedRoles` (party-facts §5 rule 2) folds the slugs another chip on the same deal already
 * entails, so a chip suffix reads `(Legal)` rather than `(Legal, Participant)`. Optional and empty by
 * default, so every existing caller keeps its exact behaviour. */
export function formatRoles(
  roles: readonly string[] | undefined, impliedRoles?: readonly string[],
): string | null {
  const ref = { implied_roles: impliedRoles as string[] | undefined };
  const meaningful = (roles ?? []).filter((slug) => rendersRole(ref, slug));
  if (meaningful.length === 0) return null;
  return meaningful.map(formatRole).join(', ');
}

/** The group heading for a role slug, or null when it should render headless. */
export function roleHeading(slug: string | null | undefined): string | null {
  return isMeaningfulRole(slug) ? formatRole(slug as string) : null;
}

/**
 * §1.5(a) — the sub-heading for a transaction side. `Buy side`, `Sell side`, `Target`, `Lender`.
 *
 * "side" is appended only where it reads naturally: "Buy side" and "Sell side" are how the desk talks
 * ("buyside banker vs sellside", the owner's own phrasing), while "Target side" and "Lender side" do
 * not — the target IS the side. `mutual` and `unknown` return null: `unknown` is the `manual_only`
 * "nobody has said", and grouping under it would imply an answer where there is none.
 */
const PARTY_HEADINGS: Record<string, string> = {
  buy: 'Buy side', sell: 'Sell side', target: 'Target', lender: 'Lender',
};

export function partyHeading(slug: string | null | undefined): string | null {
  return typeof slug === 'string' ? PARTY_HEADINGS[slug] ?? null : null;
}

/**
 * P1 — ONE heading line per group, stating the role and the side together.
 *
 * The owner's grammar is `Investment Banking · Advisor — Buy Side`: discipline, relationship type,
 * side. This renders the two segments that exist in the payload — `Investment Banking — Buy Side` —
 * and degrades exactly as the ask requires: a role with no renderable side is the role alone
 * (`Target`), and a group with no role at all has no heading at all (an `unclassified` or absent role
 * must never shout the absence of information).
 *
 * ⚠ **The `· Advisor` segment is deliberately absent, not forgotten.** It is the fact's
 * `relationship_type` (`transaction_advisor` / `deal_participation` / …), and that key is NOT on the
 * ref cell — `entity_links.scrub_ref_cell` emits `{local_id, name, open_profile, roles, role_parties,
 * title, employer}` and nothing more. It reaches the front end only on `CrmProjectedChip.facts[]`,
 * i.e. only AFTER hydration. Rendering it from there would make the hydrated heading differ from the
 * pre-hydrate one, which is precisely the "jumpy display twice thing" the owner reported and which
 * both grouping paths exist to prevent. It needs `relationship_type` on the scrubbed ref (a backend
 * payload change), and lands with no change here beyond one more segment.
 *
 * Both segments are vocabulary-derived: `formatRole` word-cases the slug algorithmically, so a new
 * discipline needs no code. The side passes through `partyHeading`, whose deliberate omissions
 * (`mutual`, `unknown`) mean "nobody has said" and render as no segment rather than as an answer.
 */
export function groupHeading(
  role: string | null | undefined, party: string | null | undefined,
): string | null {
  const head = roleHeading(role);
  if (head === null) return null;
  const side = partyHeading(party);
  return side === null ? head : `${head} — ${side}`;
}

/**
 * §1.5(a) — group an entity's roles, then its parties WITHIN each role.
 *
 * Returns `[role, [party|null, ...]]` pairs: a role with no party carries `[null]`, which renders flat
 * under the role heading. Sorted by display label so the order matches what the operator reads, and
 * stable across renders — a list that reorders between two page loads reads as data changing.
 *
 * ⚠ **A ref that states a MEANINGFUL role gets NO headless bucket** (owner item 1, 2026-08-09).
 * A ref routinely carries both — `['legal','unclassified']` is the ordinary shape once E5a's legacy
 * conversion and a real fact both touch one entity — and the old `roles.length > 0 ? roles : [null]`
 * kept `unclassified` as a bucket of its own. It renders headless (see `UNCLASSIFIED_ROLE`), so the
 * same firm appeared under `Legal` AND again in the unlabelled list at the bottom of the field: the
 * "duplicate generic participation" the owner reported. `unclassified` means "nobody has said yet",
 * and somebody has, so the bucket is not information — it is the absence of information printed
 * beside its own answer. The headless bucket survives only when NOTHING meaningful was said, which is
 * the ~387 unconverted legacy refs and must keep rendering or those people vanish from the panel.
 *
 * It lives HERE rather than in either renderer deliberately: `RoleGroupedRefs` (pre-hydrate) and
 * `ProjectedFieldRow` (hydrated) both group through this function, and a filter in one of them is
 * visible as the field re-laying itself out a moment after opening — the "jumpy display" bug.
 */
export function rolePartyPairs(
  ref: { roles?: string[]; role_parties?: { role: string; party: string }[];
         implied_roles?: string[] },
): [string | null, (string | null)[]][] {
  // Party-facts §5 rule 2 — an IMPLIED slug is folded here for the same reason `unclassified` is: it
  // is not information, it is another chip's information repeated. `rendersRole` applies the server's
  // `implied_roles` when present and the client `unclassified` rule always (see its note).
  //
  // ⚠ The headless bucket below is what stops this hiding a ROW. A ref whose every slug folds away
  // falls back to `[null]` and renders under no heading — the same treatment an unconverted legacy ref
  // gets, and the reason folding is per chip and never per row (§5 rule 3).
  const meaningful = (ref.roles ?? []).filter((slug) => rendersRole(ref, slug));
  const roles: (string | null)[] = meaningful.length > 0 ? meaningful : [null];
  const byRole = new Map<string | null, (string | null)[]>();
  for (const role of roles) {
    const parties = (ref.role_parties ?? [])
      .filter((rp) => rp.role === role && partyHeading(rp.party) !== null)
      .map((rp) => rp.party as string | null);
    // No party stated (or none renderable) ⇒ ONE flat bucket, never an "Unknown" heading.
    byRole.set(role, parties.length > 0 ? [...new Set(parties)].sort() : [null]);
  }
  return [...byRole.entries()].sort(([a], [b]) =>
    a === null ? -1 : b === null ? 1 : formatRole(a).localeCompare(formatRole(b)));
}

/**
 * The owner's `[FirstName LastName], [Title], [Org Name]` (memo §15 item 2), skipping whatever is
 * absent so there is never a dangling comma.
 *
 * Title and employer are **governed reads**, not stored snapshot text — and the employer is present
 * only when that org itself passed the entity gate, so a restricted or merged-away employer renders as
 * "no employer stated" rather than as a name. Nothing here can invent one.
 */
export function personSubtitle(
  ref: { title?: string | null; employer?: { name: string | null } | null },
): string | null {
  const parts = [
    (ref.title ?? '').trim(),
    (ref.employer?.name ?? '').trim(),
  ].filter((p) => p !== '');
  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * The deep link to an entity profile — the URL the owner's "open the profile in a new tab" needs.
 *
 * ⚠ The `sel` param spells an organization **`org`**, while the server's `open_profile.kind` says
 * **`organization`**. Two vocabularies for one concept, and mapping them in one place is the point of
 * this function: `CrmPage`'s openers are `sel.open('org:'+id)` / `sel.open('person:'+id)`, so a link
 * built with the server's word would silently resolve to no selection at all.
 */
export function profileHref(kind: 'person' | 'organization', id: number): string {
  return `/crm?sel=${kind === 'organization' ? 'org' : 'person'}:${id}`;
}

/** The deep link to ONE message's full page — the `msg` SELECTION (`?sel=msg:N`), which is the page,
 *  not the `?msg=N` modal. Beside `profileHref` for the same reason: the timeline row's "Full view ↗"
 *  is a real anchor so ⌘-click opens a tab, and the URL shape is written in one place. */
export function msgHref(emailId: number): string {
  return `/crm?sel=msg:${emailId}`;
}

/** The longest rendered line a group may hold and still flow into columns. */
export const COMPACT_LINE_MAX = 28;

/**
 * P1 — "multiple columns inside the field where entries are short".
 *
 * Measured on the RENDERED line, not on the name: a person carrying `, Title, Company` is not short
 * even when their name is, and putting one of those in each of two narrow columns wraps every row and
 * ends up TALLER than one column. So the rule is: enough rows to be worth splitting, and every row a
 * short bare label. Orgs under a role heading are the case this exists for.
 */
export function flowsInColumns(lines: readonly string[]): boolean {
  return lines.length >= 4 && lines.every((line) => line.length <= COMPACT_LINE_MAX);
}

// ── P3 — the contradiction badge ─────────────────────────────────────────────────────────────────

/** The subset of a ref this module reads. Structural, so a test fixture need not build a whole ref. */
export interface PartyRef {
  local_id?: number;
  name?: string | null;
  roles?: string[];
  role_parties?: { role: string; party: string }[];
  /** Party-facts §5 rule 2 — the subset of `roles` another chip on this deal already entails. */
  implied_roles?: string[];
  employer?: { local_id: number; name: string | null } | null;
}

/** One disagreement, as seen FROM one of the two rows that carry the badge. */
export interface SideContradiction {
  /** The discipline both are recorded at — the only axis on which they are comparable. */
  role: string;
  /** The sides recorded for the entity whose row this is. */
  ownSides: string[];
  /** The sides recorded for the other entity. */
  otherSides: string[];
  /** Whose disagreement it is: the person's employer, or a person employed by this firm. */
  counterpart: { name: string | null; relation: 'firm' | 'employee' };
}

/** Badge index key. Person and organization ids are different id spaces — one map needs both. */
export function contradictionKey(kind: 'person' | 'organization', id: number): string {
  return `${kind}:${id}`;
}

/**
 * The sides this ref is recorded on, per discipline.
 *
 * ⚠ **`partyHeading(...) === null` is dropped, and that is the guard the whole feature rests on.**
 * `unknown` is `transaction_side`'s `manual_only` "nobody has said yet" and `mutual` is "both", so
 * neither is a *position*. Counting them would make the badge fire on the absence of an answer — on
 * the ~387 `unclassified` legacy conversions and every un-sided fact on the board — which is the one
 * failure mode that would make the badge worthless. Same filter `rolePartyPairs` applies, for the
 * same reason.
 */
function sidesByRole(ref: PartyRef): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const pair of ref.role_parties ?? []) {
    if (!isMeaningfulRole(pair?.role) || partyHeading(pair?.party) === null) continue;
    const seen = out.get(pair.role) ?? new Set<string>();
    seen.add(pair.party);
    out.set(pair.role, seen);
  }
  return out;
}

/**
 * P3 — where a person's side disagrees with their firm's side at the SAME discipline.
 *
 * Purely derived from what the record payload already carries: the person's `role_parties` (§1.5(a)),
 * their `employer.local_id` (#881), and the employer's own `role_parties` from the sibling catch-all
 * column. Nothing is fetched and nothing is inferred — this only reads two facts that are already on
 * screen inches apart and says that they cannot both be true.
 *
 * Returns an index keyed by `contradictionKey`, carrying an entry for BOTH rows: the person's badge
 * names the firm, the firm's badge names the person. A firm with two disagreeing employees gets two.
 *
 * **Every one of these is a reason NOT to badge**, and each is a distinct kind of "we don't know":
 *
 *   * the person has no recorded employer, or their employer is not on this deal — there is no firm
 *     fact to disagree with, which is the common case and must stay silent;
 *   * the person has no side at that discipline, or the firm has none — absence, not conflict. This
 *     is the case the ledger is FULL of (`unknown`/`mutual`/no `role_parties` at all);
 *   * the two overlap at all. A firm advising both sides in one discipline is a real, recorded
 *     arrangement that `role_parties` exists to express — an employee on either of those sides agrees
 *     with their firm, and a naive equality test would flag the whole board.
 */
export function findSideContradictions(
  people: readonly PartyRef[], organizations: readonly PartyRef[],
): Map<string, SideContradiction[]> {
  const index = new Map<string, SideContradiction[]>();
  const add = (key: string, entry: SideContradiction) => {
    const list = index.get(key);
    if (list) list.push(entry);
    else index.set(key, [entry]);
  };

  const orgById = new Map<number, PartyRef>();
  for (const org of organizations) {
    if (typeof org.local_id === 'number') orgById.set(org.local_id, org);
  }

  for (const person of people) {
    const personId = person.local_id;
    const firmId = person.employer?.local_id;
    if (typeof personId !== 'number' || typeof firmId !== 'number') continue;
    const firm = orgById.get(firmId);
    if (firm === undefined) continue;
    const firmSides = sidesByRole(firm);
    for (const [role, mine] of sidesByRole(person)) {
      const theirs = firmSides.get(role);
      if (theirs === undefined || theirs.size === 0) continue;
      if ([...mine].some((side) => theirs.has(side))) continue;
      const own = [...mine].sort();
      const other = [...theirs].sort();
      add(contradictionKey('person', personId), {
        role, ownSides: own, otherSides: other,
        counterpart: { name: person.employer?.name ?? firm.name ?? null, relation: 'firm' },
      });
      add(contradictionKey('organization', firmId), {
        role, ownSides: other, otherSides: own,
        counterpart: { name: person.name ?? null, relation: 'employee' },
      });
    }
  }
  return index;
}

/** The sentence the hover card shows — names the discipline, both positions, and who holds the other. */
export function contradictionSummary(entry: SideContradiction): string {
  const label = (sides: string[]) => sides.map((s) => partyHeading(s) ?? formatRole(s)).join(' / ');
  const who = entry.counterpart.name
    ?? (entry.counterpart.relation === 'firm' ? 'their firm' : 'someone at this firm');
  const other = entry.counterpart.relation === 'firm'
    ? `their firm ${who} is recorded as ${label(entry.otherSides)}`
    : `${who} at this firm is recorded as ${label(entry.otherSides)}`;
  return `${formatRole(entry.role)} — recorded here as ${label(entry.ownSides)}, but ${other}.`;
}
